import React, { ComponentType, useEffect } from 'react';

import { injectIntl, InjectedIntl } from 'react-intl';

import Avatar from '@atlaskit/avatar';
import { PopupMenuGroup } from '@atlaskit/menu';
import { type ThemeColorModes } from '@atlaskit/tokens';

import { AnalyticsEvent } from '../types';
import urls from '../urls';

import { AccountMenuWorkspaces } from './account-menu-workspaces';
import messages from './account-menu.i18n';
import {
  NonInteractiveDropdownItem,
  ProfileWrapper,
  AvatarWrapper,
  DisplayName,
} from './account-menu.style';
import { DropdownItem, DropdownItemGroup } from './dropdown-helpers';
import { ThemeSelector } from './theme-selector/theme-selector';
import { handleGlobalThemeChange } from './theme-selector/utils';

export type AccountMenuCurrentUser = {
  is_staff?: boolean;
  account_id?: string;
  extra?: { has_workspace?: boolean };
};

export type AccountMenuUserProps = {
  loginUrl?: string;
  logoutUrl?: string;
  switchAccountUrl?: string;
  manageAccountUrl?: string;
  userEmail?: string | null;
  userAvatarUrl: string | undefined;
  userDisplayName: string | undefined;
  userUuid: string | undefined;
  userWorkspaceId: string | undefined;
  currentUser?: AccountMenuCurrentUser;
  workspaceLinkComponent?: ComponentType<any>;
  isOrgAdmin?: boolean;
  workspaceOrgId?: string;
};

export type AccountMenuThemeSelectorProps = {
  onThemeSelect?: () => void;
};

export type AccountMenuProps = AccountMenuUserProps & {
  recentlyViewedWorkspacesKeys?: string[];
  isAkDropdown?: boolean;
  publishUiEvent: (event: AnalyticsEvent) => void;
  isNavThemeSelectorEnabled?: boolean;
};

type Props = AccountMenuProps & {
  intl: InjectedIntl;
  themeSelectorProps: AccountMenuThemeSelectorProps;
};

const AccountMenu = React.memo((props: Props): any => {
  const { publishUiEvent, switchAccountUrl } = props;

  useEffect(() => {
    publishUiEvent({
      action: 'opened',
      actionSubject: 'menu',
      actionSubjectId: 'accountMenu',
      source: 'navigation',
      attributes: { isSwitchAccountLinkEnabled: !!switchAccountUrl },
    });
  }, [publishUiEvent, switchAccountUrl]);

  const handleSwitchAccountClick = () => {
    props.publishUiEvent({
      action: 'clicked',
      actionSubject: 'link',
      actionSubjectId: 'switchAccountLink',
      source: 'navigation',
    });
  };

  const getAnonymousMenuItems = () => {
    const { intl, isAkDropdown } = props;

    return (
      <PopupMenuGroup minWidth={234} maxWidth={800}>
        <DropdownItem
          isAkDropdown={isAkDropdown}
          href={urls.ui.login()}
          key="login-link"
        >
          {intl.formatMessage(messages.logInLink)}
        </DropdownItem>
        <DropdownItem
          isAkDropdown={isAkDropdown}
          href={urls.ui.signup()}
          key="signup-link"
        >
          {intl.formatMessage(messages.signUpLink)}
        </DropdownItem>
      </PopupMenuGroup>
    );
  };

  const getAuthenticatedMenuItems = () => {
    const {
      loginUrl,
      logoutUrl,
      manageAccountUrl,
      userAvatarUrl,
      userWorkspaceId,
      userDisplayName,
      userEmail,
      intl,
      isAkDropdown,
      recentlyViewedWorkspacesKeys,
      workspaceLinkComponent,
      themeSelectorProps,
      isNavThemeSelectorEnabled = false,
    } = props;

    if (!userDisplayName) {
      return [];
    }

    const workspaceSlug = userWorkspaceId;

    const { onThemeSelect } = themeSelectorProps;

    return (
      <PopupMenuGroup minWidth={234} maxWidth={800}>
        {loginUrl && userEmail && (
          <DropdownItemGroup
            title={intl.formatMessage(messages.accountHeader)}
            isAkDropdown={isAkDropdown}
          >
            <NonInteractiveDropdownItem isAkDropdown={isAkDropdown}>
              <ProfileWrapper>
                <AvatarWrapper>
                  <Avatar
                    appearance="circle"
                    size="medium"
                    src={userAvatarUrl}
                  />
                </AvatarWrapper>
                <div>
                  <DisplayName>{userDisplayName}</DisplayName>
                  <small>{userEmail}</small>
                </div>
              </ProfileWrapper>
            </NonInteractiveDropdownItem>
            {switchAccountUrl && (
              <DropdownItem
                isAkDropdown={isAkDropdown}
                href={switchAccountUrl}
                onClick={handleSwitchAccountClick}
              >
                {intl.formatMessage(messages.switchAccountLink)}
              </DropdownItem>
            )}
            {isNavThemeSelectorEnabled && (
              <ThemeSelector
                onThemeChange={(value: ThemeColorModes) => {
                  handleGlobalThemeChange(value);
                  onThemeSelect?.();
                }}
                isBeta
              />
            )}
          </DropdownItemGroup>
        )}
        {recentlyViewedWorkspacesKeys && (
          <AccountMenuWorkspaces
            key="workspaces"
            isAkDropdown={isAkDropdown}
            userWorkspaceSlug={workspaceSlug}
            recentlyViewedWorkspacesUuids={recentlyViewedWorkspacesKeys}
            workspaceLinkComponent={workspaceLinkComponent}
            hasSeparator={!!manageAccountUrl}
          />
        )}

        {logoutUrl && (
          <DropdownItemGroup
            isAkDropdown={isAkDropdown}
            key="authenticated-user-group"
            hasSeparator
          >
            <DropdownItem isAkDropdown={isAkDropdown} href={logoutUrl}>
              {intl.formatMessage(messages.logOutLink)}
            </DropdownItem>
          </DropdownItemGroup>
        )}
      </PopupMenuGroup>
    );
  };

  return props.userDisplayName !== undefined
    ? getAuthenticatedMenuItems()
    : getAnonymousMenuItems();
});

export default injectIntl(AccountMenu);
